/**
 * galeria.ts — Categoria "Galeria" (10 modelos premium)
 *
 * Conjuntos de fotos montados com seção em camadas + `ui.image`,
 * sempre usando o acervo compartilhado (`acervo://`).
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para uma foto da galeria. */
function foto(src: string, alt: string, proporcao = 'square', raio = 'xl', sombra = 'md') {
  return b('ui.image', { src, alt, ratio: proporcao, radius: raio, shadow: sombra, motion: 'fade-up' });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'galeria.tres-quadradas',
    category: 'galeria',
    label: 'Três fotos quadradas',
    description: 'Grade simples com três fotos e cantos arredondados.',
    preview: 'gallery',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade' }, [
        foto(FOTO.loja, 'Loja da empresa'),
        foto(FOTO.produto, 'Produto em destaque'),
        foto(FOTO.interior, 'Ambiente interno'),
      ]),
    ],
  },

  {
    key: 'galeria.com-titulo',
    category: 'galeria',
    label: 'Galeria com título',
    description: 'Título de seção acima de quatro fotos.',
    preview: 'gallery',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Um pouco do nosso trabalho', size: 'lg', level: 'h2', align: 'center' }),
        foto(FOTO.criativo, 'Equipe criativa'),
        foto(FOTO.mesaTrabalho, 'Mesa de trabalho'),
        foto(FOTO.escritorio, 'Escritório'),
        foto(FOTO.time, 'Time reunido'),
      ]),
    ],
  },

  {
    key: 'galeria.mosaico',
    category: 'galeria',
    label: 'Mosaico com destaque',
    description: 'Uma foto grande acompanhada de duas menores.',
    preview: 'gallery',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade' }, [
        foto(FOTO.arquitetura, 'Fachada da empresa', 'wide', 'xl', 'lg'),
        foto(FOTO.interior, 'Ambiente interno'),
        foto(FOTO.atendimento, 'Atendimento ao cliente'),
      ]),
    ],
  },

  {
    key: 'galeria.foto-unica',
    category: 'galeria',
    label: 'Foto única panorâmica',
    description: 'Uma foto larga com sombra forte e cantos arredondados.',
    preview: 'gallery',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', motion: 'scale' }, [
        foto(FOTO.palestra, 'Evento da empresa', 'wide', 'xl', 'lg'),
      ]),
    ],
  },

  {
    key: 'galeria.escura',
    category: 'galeria',
    label: 'Galeria em fundo escuro',
    description: 'Fundo escuro com brilho, fotos em destaque.',
    preview: 'gallery',
    outputs: WEB,
    order: 5,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundGradient: 'mesh-dark', glow: 'top-right', textColor: '#ffffff', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Bastidores', size: 'md', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          foto(FOTO.industria, 'Produção'),
          foto(FOTO.logistica, 'Logística'),
          foto(FOTO.dados, 'Sala de controle'),
        ],
      ),
    ],
  },

  {
    key: 'galeria.com-texto',
    category: 'galeria',
    label: 'Galeria com texto ao lado',
    description: 'Duas fotos à direita e explicação à esquerda.',
    preview: 'split-right',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' }, [
        b('ui.title', { text: 'Ambientes preparados para receber você', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Estrutura pensada no conforto de quem visita e na produtividade de quem trabalha.' }),
        foto(FOTO.interior, 'Recepção'),
        foto(FOTO.escritorio, 'Sala de reunião'),
      ]),
    ],
  },

  {
    key: 'galeria.retratos',
    category: 'galeria',
    label: 'Fotos em retrato',
    description: 'Três fotos verticais, boas para pessoas e serviços.',
    preview: 'grid-3',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', layout: 'bento', pattern: 'dots', motion: 'fade-up' }, [
        foto(FOTO.retrato, 'Profissional da equipe', 'portrait'),
        foto(FOTO.consultoria, 'Atendimento consultivo', 'portrait'),
        foto(FOTO.equipe, 'Equipe em ação', 'portrait'),
      ]),
    ],
  },

  {
    key: 'galeria.produtos',
    category: 'galeria',
    label: 'Vitrine de produtos',
    description: 'Quatro fotos quadradas com selo de vitrine.',
    preview: 'grid-4',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundGradient: 'soft', motion: 'fade' }, [
        b('ui.badge', { text: 'Vitrine', variant: 'soft', align: 'center' }),
        foto(FOTO.produto, 'Produto 1'),
        foto(FOTO.loja, 'Produto 2'),
        foto(FOTO.celular, 'Produto 3'),
        foto(FOTO.minimal, 'Produto 4'),
      ]),
    ],
  },

  {
    key: 'galeria.antes-depois',
    category: 'galeria',
    label: 'Duas fotos comparativas',
    description: 'Duas fotos lado a lado, boas para antes e depois.',
    preview: 'grid-2',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.title', { text: 'Antes e depois', size: 'md', level: 'h2', align: 'center' }),
        foto(FOTO.mesaTrabalho, 'Antes', 'landscape'),
        foto(FOTO.painel, 'Depois', 'landscape'),
      ]),
    ],
  },

  {
    key: 'galeria.com-convite',
    category: 'galeria',
    label: 'Galeria com convite',
    description: 'Fotos seguidas de um botão para ver mais.',
    preview: 'gallery',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', layout: 'bento', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' }, [
        foto(FOTO.criativo, 'Projeto entregue'),
        foto(FOTO.arquitetura, 'Obra concluída'),
        b('ui.buttons', { items: 'Ver todos os projetos|#contato|solid', align: 'center' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
